/**
 * A hash router in forty lines.
 *
 * §1.4 step 3 names the destination explicitly — "navigates to
 * `/#/pillar/{code}`" — so the location has to carry the view. Hash routing
 * rather than history routing because the server already serves the SPA shell
 * for any path and a hash never reaches it, which keeps deep links working
 * behind any static host without a rewrite rule.
 *
 * React Router would add a dependency for five routes. This does not.
 */
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'dashboard' }
  | { name: 'pillar'; code: string }
  | { name: 'projects' }
  | { name: 'project'; id: string }
  | { name: 'logs' }
  | { name: 'documents' }
  | { name: 'photos' }
  | { name: 'procurement' }
  | { name: 'schedule' }
  | { name: 'contacts' }
  | { name: 'review' }
  | { name: 'merges' };

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return { name: 'dashboard' };
  switch (parts[0]) {
    case 'pillar':
      return parts[1] ? { name: 'pillar', code: parts[1].toUpperCase() } : { name: 'dashboard' };
    case 'projects':
      return parts[1] ? { name: 'project', id: parts[1] } : { name: 'projects' };
    case 'logs':
      return { name: 'logs' };
    case 'documents':
      return { name: 'documents' };
    case 'photos':
      return { name: 'photos' };
    case 'procurement':
      return { name: 'procurement' };
    case 'schedule':
      return { name: 'schedule' };
    case 'contacts':
      return { name: 'contacts' };
    case 'review':
      return { name: 'review' };
    case 'merges':
      return { name: 'merges' };
    default:
      return { name: 'dashboard' };
  }
}

export function navigate(to: string): void {
  window.location.hash = to.startsWith('#') ? to : `#${to}`;
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
